import { Outlet, NavLink, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/auth";
import { api } from "@/api";
import { isProfileComplete } from "@/lib/profile";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, Megaphone, FileText, MessageSquare, User, Settings as SettingsIcon,
  Users, PlusCircle, Percent, Sparkles, LogOut, Menu, X,
} from "lucide-react";

const CREATOR_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/dashboard/kampanjat", label: "Kampanjat", icon: Megaphone },
  { to: "/dashboard/hakemukset", label: "Hakemukset", icon: FileText },
  { to: "/dashboard/viestit", label: "Viestit", icon: MessageSquare },
  { to: "/dashboard/profiili", label: "Oma profiili", icon: User },
  { to: "/dashboard/asetukset", label: "Asetukset", icon: SettingsIcon },
];

const BUSINESS_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/dashboard/sisallontuottajat", label: "Sisällöntuottajat", icon: Users },
  { to: "/dashboard/julkaise", label: "Julkaise kampanja", icon: PlusCircle },
  { to: "/dashboard/julkaise-affiliate", label: "Julkaise affiliate", icon: Percent },
  { to: "/dashboard/omat-kampanjat", label: "Omat kampanjat", icon: Megaphone },
  { to: "/dashboard/saapuneet-hakemukset", label: "Hakemukset", icon: FileText },
  { to: "/dashboard/viestit", label: "Viestit", icon: MessageSquare },
  { to: "/dashboard/profiili", label: "Yritysprofiili", icon: User },
  { to: "/dashboard/asetukset", label: "Asetukset", icon: SettingsIcon },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const isBizPro = user.role === "business" && user.profile?.is_pro;
  const businessNav = isBizPro ? BUSINESS_NAV : BUSINESS_NAV.filter((i) => !i.to.endsWith("sisallontuottajat"));
  const nav = user.role === "creator" ? CREATOR_NAV : businessNav;

  const loadUnread = useCallback(async () => {
    try { const { data } = await api.get("/messages/unread/count"); setUnread(data.count || 0); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, 20000);
    const onRefresh = () => loadUnread();
    window.addEventListener("unread-refresh", onRefresh);
    return () => { clearInterval(id); window.removeEventListener("unread-refresh", onRefresh); };
  }, [loadUnread]);

  // Onboarding gate: an incomplete profile must be filled before using the dashboard.
  const onProfilePage = location.pathname === "/dashboard/profiili";
  if (!isProfileComplete(user) && !onProfilePage) {
    return <Navigate to="/dashboard/profiili" replace />;
  }

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const initials = (user.name || user.email).slice(0, 2).toUpperCase();

  const SidebarContent = () => (
    <>
      <Link to="/" className="px-2 py-2 mb-3"><Logo variant="light" className="h-6 w-auto" /></Link>
      <nav className="flex-1 space-y-1">
        {nav.map((item) => {
          const isMsg = item.to.endsWith("viestit");
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.to.split("/").pop() || "home"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive ? "bg-[#D5E5F9] text-[#292827] font-medium" : "text-[#3A3733] hover:text-[#292827] hover:bg-[#F3F1EE]"
                }`
              }
            >
              <item.icon size={18} strokeWidth={1.75} />
              <span className="flex-1">{item.label}</span>
              {isMsg && unread > 0 && (
                <span data-testid="nav-unread-badge"
                  className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#292827] text-white text-[11px] font-medium flex items-center justify-center">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <button onClick={doLogout} data-testid="logout-btn"
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#3A3733] hover:text-[#292827] hover:bg-[#F3F1EE] transition-colors">
        <LogOut size={18} strokeWidth={1.75} /> Kirjaudu ulos
      </button>
    </>
  );

  return (
    <div className="min-h-screen brand-page-gradient">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E6E1D9] px-4 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center"><Logo variant="light" className="h-5 w-auto" testid="logo-mobile" /></Link>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu strokeWidth={1.75} /></button>
      </div>

      <div className="flex max-w-[1500px] mx-auto">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-60 shrink-0 p-4 h-screen sticky top-0 bg-white border-r border-[#E6E1D9]">
          <SidebarContent />
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/20" onClick={() => setOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white p-4 flex flex-col">
              <button className="self-end mb-2" onClick={() => setOpen(false)}><X strokeWidth={1.75} /></button>
              <SidebarContent />
            </aside>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0 px-5 md:px-10 py-8 md:py-12">
          {location.pathname === "/dashboard" && (
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-[13px] text-[#968F85]">
                  {user.role === "creator" ? "Sisällöntuottaja" : "Yritys"}
                </p>
                <h2 className="text-2xl md:text-[28px] font-semibold tracking-display text-[#292827] mt-1">Hei, {user.name?.split(" ")[0] || "käyttäjä"}</h2>
              </div>
              <div className="h-10 w-10 rounded-full bg-[#292827] flex items-center justify-center font-medium text-white text-sm overflow-hidden" data-testid="user-avatar">
                {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : initials}
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
